import { Transition } from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import React from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from "react-beautiful-dnd";
import { Link } from "react-router-dom";
import CommonSwitch from "../../common/components/switch";
import Error from "../../common/components/error";
import Loading from "../../common/components/loading";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import { useTitle } from "../../common/utils/title";
import {
  BobQuickMessageConfigFragment,
  useBobQuickMessageConfigsQuery,
  useReorderBobQuickMessagesMutation,
  useSetBobQuickMessageEnabledMutation,
} from "../../graphql/generated";

const BobQuickMessageList: React.FC = () => {
  useTitle("Bob Quick Messages");
  const { notifier } = React.useContext(NotificationContext);

  const [{ data, error }] = useBobQuickMessageConfigsQuery({
    requestPolicy: "cache-and-network",
  });
  const [{ fetching: reordering }, reorderMutation] =
    useReorderBobQuickMessagesMutation();
  const [, setEnabledMutation] = useSetBobQuickMessageEnabledMutation();

  // Local order lets a drag reflect immediately; the server list re-seeds it when the
  // query resolves or the reorder mutation returns the persisted order.
  const [order, setOrder] = React.useState<BobQuickMessageConfigFragment[]>([]);
  React.useEffect(() => {
    if (data?.bobQuickMessageConfigs) {
      setOrder(data.bobQuickMessageConfigs);
    }
  }, [data]);

  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      if (reordering) return;
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const reordered = Array.from(order);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);
      setOrder(reordered);

      reorderMutation({ ids: reordered.map((config) => config.id) }).then(
        notifier.notifyGraphql("Reordered")
      );
    },
    [order, reordering, reorderMutation, notifier]
  );

  const toggleEnabled = React.useCallback(
    (config: BobQuickMessageConfigFragment) => {
      setEnabledMutation({ id: config.id, enabled: !config.enabled }).then(
        notifier.notifyGraphql()
      );
    },
    [setEnabledMutation, notifier]
  );

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Bob Quick Messages
              <span className="text-sm text-gray-500 px-2">
                {order.length}
              </span>
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Drag to reorder. Toggle to show or hide a quick message. Edit to
              override its title, subtitle, icon, or gate it behind a feature
              flag.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="bob-quick-messages">
                {(provided: DroppableProvided) => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="divide-y divide-gray-200"
                  >
                    {order.map((config, index) => (
                      <Draggable
                        key={config.id}
                        draggableId={config.id}
                        index={index}
                      >
                        {(
                          dragProvided: DraggableProvided,
                          snapshot: DraggableStateSnapshot
                        ) => (
                          <li
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={classNames(
                              snapshot.isDragging ? "shadow-md" : "",
                              "flex items-center gap-3 bg-white px-4 py-3"
                            )}
                          >
                            <span
                              {...dragProvided.dragHandleProps}
                              className="text-gray-400 hover:text-gray-600 cursor-grab"
                            >
                              <Bars3Icon className="h-5 w-5" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p
                                className={classNames(
                                  config.enabled
                                    ? "text-gray-900"
                                    : "text-gray-400",
                                  "text-sm font-medium truncate"
                                )}
                              >
                                {config.name}
                              </p>
                              <p className="text-sm text-gray-500 truncate">
                                {config.category}
                                {config.featureFlagId ? (
                                  <>
                                    {" · flag: "}
                                    <Link
                                      to={`/feature_flags/${config.featureFlagId}`}
                                      className="text-indigo-600 hover:text-indigo-500"
                                    >
                                      {config.featureFlagId}
                                    </Link>
                                  </>
                                ) : (
                                  ""
                                )}
                              </p>
                            </div>
                            <CommonSwitch
                              label={config.enabled ? "Enabled" : "Disabled"}
                              enabled={config.enabled}
                              toggle={() => toggleEnabled(config)}
                            />
                            <Link
                              to={`/bob_quick_messages/${config.id}/edit`}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                            >
                              Edit
                            </Link>
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default BobQuickMessageList;
